import { Modal } from 'antd';
import Axios from 'axios';
import moment from 'moment';
import { HIDE_MESSAGE_401_PARAMS } from './config';
import i18n, { DEFAULT_LANGUAGE } from './helpers/i18n';
import authService from './services/authService';
import * as apiSpec from './__generated';

// interceptors
let isRefreshing: boolean = false;
const waitForRefreshTokenFinish = () => {
  return new Promise(resolve => {
    const interval = window.setInterval(() => {
      if (!isRefreshing) {
        if (interval) {
          window.clearInterval(interval);
        }
        resolve(null);
      }
    }, 50);
  });
};

const handleResponse401Interceptors = async (err: any) => {
  const { config, response } = err;
  if (response?.status === 401) {
    const logout = () => {
      const noDisplayMessage = (config?.params || {})[HIDE_MESSAGE_401_PARAMS];
      if (noDisplayMessage) {
        authService.clearOnLogout();
        window.location.reload();
        return Promise.reject(err);
      } else {
        authService.clearOnLogout();
        Modal.error({
          title: i18n.t('LoginSessionExpiredTitle') as any,
          content: i18n.t('LoginSessionExpiredContent') as any,
          onOk: () => {
            window.location.reload();
          },
        });
        return Promise.reject(err);
      }
    };
    if (!isRefreshing) {
      isRefreshing = true;
      const refreshToken = authService.getRefreshToken();
      const loginInfo = authService.getLoginInfo();
      if (!refreshToken) {
        return logout();
      }

      try {
        const response = await apiSdk.AuthService.refreshToken({
          body: {
            refreshToken,
            userId: loginInfo?.userId,
          },
        });
        if (response.success) {
          const expiredTime = moment(new Date()).add(response.expiryDuration, 'minutes');
          authService.saveLoginInfo({
            token: response.accessToken || '',
            expiredTime,
            userId: response.userId || '',
          });
          authService.saveRefreshToken(response.refreshToken || '');
          config.headers.Authorization = `Bearer ${response.accessToken}`;
          config.headers['culture-code'] = i18n.language?.toLowerCase() || DEFAULT_LANGUAGE;

          isRefreshing = false;
          return Axios.request(config);
        } else {
          return logout();
        }
      } catch (error) {
        return logout();
      }
    } else {
      // other request call renew token
      await waitForRefreshTokenFinish();
      const token = authService.getAccessToken();
      config.headers.Authorization = token ? `Bearer ${token}` : '';
      config.headers['culture-code'] = i18n.language?.toLowerCase() || DEFAULT_LANGUAGE;

      return Axios.request(config);
    }
  } else {
    return Promise.reject(err);
  }
};

const httpClient = Axios.create({
  baseURL: `${process.env.REACT_APP_API_ENDPOINT || ''}/api`,
  paramsSerializer: (params: any) => {
    const _params = new URLSearchParams();
    if (params) {
      Object.keys(params).forEach((k: string) => {
        if (Array.isArray(params[k])) {
          params[k]?.forEach(c => {
            _params.append(k, c);
          });
        } else if (params[k] !== undefined) {
          _params.append(k, params[k]);
        }
      });
    }
    return _params as any;
  },
  validateStatus: status => {
    return status >= 200 && status < 400;
  },
});

httpClient.interceptors.request.use(config => {
  const token = authService.getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = token ? `Bearer ${token}` : '';
    config.headers['culture-code'] = i18n.language?.toLowerCase() || DEFAULT_LANGUAGE;
  }
  return config;
});
httpClient.interceptors.response.use(res => res, handleResponse401Interceptors);
export default httpClient;
// axios codegen

const apiSdkAxios = Axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT || '',
  paramsSerializer: (params: any) => {
    const _params = new URLSearchParams();
    if (params) {
      Object.keys(params).forEach((k: string) => {
        if (Array.isArray(params[k])) {
          params[k]?.forEach(c => {
            _params.append(k, c);
          });
        } else if (params[k] !== undefined) {
          _params.append(k, params[k]);
        }
      });
    }
    return _params as any;
  },
  validateStatus: status => {
    return status >= 200 && status < 400;
  },
});
apiSdkAxios.interceptors.request.use(config => {
  const token = authService.getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = token ? `Bearer ${token}` : '';
    config.headers['culture-code'] = i18n.language?.toLowerCase() || DEFAULT_LANGUAGE;
  }
  return config;
});
apiSdkAxios.interceptors.response.use(res => res, handleResponse401Interceptors);

apiSpec.serviceOptions.axios = apiSdkAxios;
export const apiSdk = apiSpec;
